import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Checkcircle, Column } from '~/commonComponents';
import {
  LEGACY_SIGNUP_DESIGN_DATE,
  POSTHOG_EVENTS,
  trackPosthogEvent,
  trackPosthogPageView,
} from '~/services/posthog';
import { editUser, saveWorkerClassification, showRegistrationPopup } from '../../../../actions';

import ConfirmPopup from '../../../shared/ConfirmPopup';
import { OutlineWarning01Icon } from '@tempmee/react-icons';

export const WorkerClassificationSelection = ({ isRegistration }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const { selectedChildOffice } = user;
  const userClassification = user?.workerClassification?.classificationType;
  const [workerClassification, setWorkerClassification] = useState(userClassification || '');
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [showNoneSelectedPopup, setShowNoneSelectedPopup] = useState(false);

  const handleConfirmButton = () => {
    trackPosthogEvent(POSTHOG_EVENTS.DENTAL_PORTAL_OFFICE_SIGNUP_COMPLETED, {
      design_date: LEGACY_SIGNUP_DESIGN_DATE,
    });
    dispatch(editUser({ data: { is_profile_complete: true } }));
    dispatch(showRegistrationPopup());
  };

  const handleSaveWorkerClassification = () => {
    dispatch(saveWorkerClassification(workerClassification));
    setShowConfirmationPopup(false);
    trackPosthogEvent(POSTHOG_EVENTS.DENTAL_PORTAL_OFFICE_SIGNUP_COMPLETED, {
      design_date: LEGACY_SIGNUP_DESIGN_DATE,
    });
  };

  useEffect(() => {
    trackPosthogPageView('/registration/worker-classification');
  }, []);

  const description = useMemo(() => {
    const isContracted = workerClassification === 'contractor';
    const isEmployed = workerClassification === 'employee';

    return (
      <Column>
        <Typography as="span" color={colors.neutral[500]}>
          Worker classification is an important choice for using GoTu. Please confirm that your
          selection is {isContracted ? 'contracted' : 'employed'} professionals at{' '}
          {selectedChildOffice?.office_name}.
        </Typography>
        {isEmployed && (
          <Typography as="span" color={colors.neutral[500]} style={{ marginTop: 20 }}>
            Please note: There is an additional fee associated with the employee option (20%
            professional pay).
          </Typography>
        )}
      </Column>
    );
  }, [selectedChildOffice?.office_name, workerClassification]);

  const Option = ({ title, value }) => (
    <button
      data-cy={`input-wc-options-${value}`}
      type="button"
      style={{
        all: 'unset',
        display: 'flex',
        cursor: 'pointer',
        marginBottom: 22,
        alignItems: 'center',
      }}
      onClick={() => setWorkerClassification(value)}
    >
      <Checkcircle checked={workerClassification === value} />

      <p className="global_font f-dark" style={{ margin: '0px 0px 0px 22px' }}>
        {title}
      </p>
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <span
        style={{
          fontFamily: 'Nunito',
          fontSize: 22,
          fontWeight: 700,
          textAlign: 'center',
          color: colors.primary[500],
          marginTop: 20,
          marginBottom: 20,
        }}
      >
        Worker Type Selection
      </span>

      <p
        style={{
          marginBottom: 25,
          fontFamily: 'Nunito',
          fontSize: 18,
          fontWeight: 700,
          textAlign: 'center',
          color: colors.neutral[600],
        }}
      >
        Please select a Worker Type for this Office:
      </p>

      <div>
        <Option title="Contractor option" value="contractor" />
        <Option title="Employee option" value="employee" />
        {isRegistration && <Option title="I'm not sure. I'll decide later" value="none" />}
      </div>

      <Typography
        italic
        color={colors.neutral[600]}
        style={{ textAlign: 'center', marginBottom: 40 }}
      >
        Disclaimer: GoTu is not providing legal or tax advice. It is up to the office's discretion
        to determine
        <br />
        which worker type best suits their needs.
      </Typography>

      <Button
        data-cy="btn-continue"
        type="button"
        size="lg"
        disabled={!workerClassification}
        onClick={() => {
          if (workerClassification === 'none') {
            trackPosthogEvent(POSTHOG_EVENTS.DENTAL_PORTAL_OFFICE_SIGNUP_WORKER_TYPE_PENDING, {
              workerType: 'pending',
            });
            setShowNoneSelectedPopup(true);
          } else {
            setShowConfirmationPopup(true);
            trackPosthogEvent(POSTHOG_EVENTS.DENTAL_PORTAL_OFFICE_SIGNUP_WORKER_TYPE_SUBMITTED, {
              design_date: LEGACY_SIGNUP_DESIGN_DATE,
            });
          }
        }}
      >
        Continue
      </Button>

      {showConfirmationPopup && (
        <ConfirmPopup
          dataCy="save-wc-confirmation-popup"
          Icon={() => <OutlineWarning01Icon color={colors.warning[500]} />}
          title="Please Confirm"
          description={description}
          descriptionStyle={{ marginBottom: 45 }}
          containerStyle={{ paddingTop: 45, paddingBottom: 50 }}
          leftButtonText="Go Back"
          leftButtonAction={() => setShowConfirmationPopup(false)}
          rightButtonText="Confirm"
          rightButtonAction={handleSaveWorkerClassification}
        />
      )}

      {showNoneSelectedPopup && (
        <ConfirmPopup
          dataCy="wc-none-selected-popup"
          closePopup={() => setShowNoneSelectedPopup(false)}
          Icon={() => <OutlineWarning01Icon color={colors.warning[500]} />}
          title="Worker Type Not Selected"
          description="Please note, this office will not be able to post shifts until a worker type is selected. You can submit your selection at a later time by navigating to Account > Worker Type."
          descriptionStyle={{ marginBottom: 45 }}
          leftButtonText="Go Back"
          leftButtonAction={() => setShowNoneSelectedPopup(false)}
          containerStyle={{ paddingTop: 45, paddingBottom: 50 }}
          rightButtonText="Confirm"
          rightButtonAction={handleConfirmButton}
        />
      )}
    </div>
  );
};
